import { useState, useRef, useEffect, useLayoutEffect, useContext, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './AIChat.css';
import { aiserverbaselink } from '../../../Env_Variables';
import { Routedispatcher } from '../../../Routedispatcher';
import { Loggedincontext } from '../../../Loggedincontext';
import { LanguageContext } from '../../../LanguageContext';
import { executeToolCalls } from './aiTools';
import MarkdownMessage from './MarkdownMessage';
import ImageStudio from './ImageStudio';
import VoiceMode, { CallIcon } from './VoiceMode';
import { isAiVoiceEvalInstitute } from './aiAllowedInstitutes';
import {
    aiInstituteHeaders,
    setAiSessionInstituteId,
    setAiSessionInstituteName,
    setAiSessionUserId,
    setAiSessionUserName,
    resolveAiUserId,
    aiConversationStorageKey,
} from './aiInstituteHeaders';

import TabexawyEvalPanel from './TabexawyEvalPanel';
import OpenAiTracePanel, { mergeTraces } from './OpenAiTracePanel';
import { usageFooterText, formatCostEgp } from './usageDisplay';
import { BsPaperclip, BsMicFill, BsTrash3, BsSendFill } from 'react-icons/bs';
import {
    convertAndImportCatalogFile,
    IMPORT_FILE_ACCEPT,
    IMPORT_KINDS,
    importKindLabel,
    validateCatalogImportFile,
} from './importUserCatalogFile';
import {
    pickRecorderMime,
    formatNoteTimer,
    transcribeVoiceNote,
    createLiveAnalyser,
    mergePeaks,
    fallbackPeaks,
    VOICE_NOTE_MAX_MS,
} from './voiceNote';

const MESSAGE_PAGE_SIZE = 10;

/** Geometric Tabexawy mark — not a plain letter. */
function TabexawyMark({ className = '', size = 22 }) {
    const uid = `tbxMark-${size}-${Math.random().toString(36).slice(2, 8)}`;
    return (
        <svg
            className={className}
            width={size}
            height={size}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <path
                d="M7 8.25h18c.69 0 1.25.56 1.25 1.25v1.9c0 .69-.56 1.25-1.25 1.25h-6.35v10.1c0 .69-.56 1.25-1.25 1.25h-2.8c-.69 0-1.25-.56-1.25-1.25v-10.1H7c-.69 0-1.25-.56-1.25-1.25V9.5c0-.69.56-1.25 1.25-1.25Z"
                fill="currentColor"
            />
            <path
                d="M9.2 9.9h13.6v1.15H17.1v11.05h-2.2V11.05H9.2V9.9Z"
                fill={`url(#${uid})`}
                opacity="0.35"
            />
            <circle cx="23.4" cy="9.1" r="1.55" fill="#EAC435" />
            <defs>
                <linearGradient id={uid} x1="9" y1="9" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#fff" />
                    <stop offset="1" stopColor="#fff" stopOpacity="0" />
                </linearGradient>
            </defs>
        </svg>
    );
}

/** Sum usage across tool rounds — each /chat request only reports that request's tokens. */
function mergeUsage(a, b) {
    if (!a && !b) return null;
    if (!a) return b;
    if (!b) return a;
    const prompt = (Number(a.prompt_tokens) || 0) + (Number(b.prompt_tokens) || 0);
    const completion = (Number(a.completion_tokens) || 0) + (Number(b.completion_tokens) || 0);
    const cached = (Number(a.cached_tokens) || 0) + (Number(b.cached_tokens) || 0);
    const cacheWrite = (Number(a.cache_write_tokens) || 0) + (Number(b.cache_write_tokens) || 0);
    const reasoning = (Number(a.reasoning_tokens) || 0) + (Number(b.reasoning_tokens) || 0);
    const cost = (Number(a.cost_usd) || 0) + (Number(b.cost_usd) || 0);
    const total =
        prompt + completion || (Number(a.total_tokens) || 0) + (Number(b.total_tokens) || 0);
    return {
        ...b,
        ...a,
        prompt_tokens: prompt,
        completion_tokens: completion,
        cached_tokens: cached,
        cache_write_tokens: cacheWrite,
        reasoning_tokens: reasoning,
        total_tokens: total,
        cost_usd: Number(cost.toFixed(8)),
        cost_usd_display: `$${cost.toFixed(6)}`,
        model: b.model || a.model || null,
    };
}

function parseSseChunk(chunk, onEvent) {
    const blocks = chunk.split('\n\n');
    for (const block of blocks) {
        if (!block.trim()) continue;

        let eventName = 'message';
        const dataLines = [];

        for (const line of block.split('\n')) {
            if (line.startsWith('event:')) {
                eventName = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trim());
            }
        }

        if (!dataLines.length) continue;

        let parsed;
        try {
            parsed = JSON.parse(dataLines.join('\n'));
        } catch (err) {
            console.error('Failed to parse SSE data', err);
            continue;
        }
        onEvent(eventName, parsed);
    }
}

async function consumeSse(response, handleEvent, signal) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    const onAbort = () => {
        try {
            reader.cancel();
        } catch (_) {
            /* ignore */
        }
    };
    if (signal) {
        if (signal.aborted) {
            onAbort();
            throw new DOMException('Aborted', 'AbortError');
        }
        signal.addEventListener('abort', onAbort, { once: true });
    }

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop() || '';

            for (const part of parts) {
                parseSseChunk(part + '\n\n', handleEvent);
            }
        }

        if (buffer.trim()) {
            parseSseChunk(buffer + '\n\n', handleEvent);
        }
    } finally {
        if (signal) signal.removeEventListener('abort', onAbort);
    }
}

function friendlyToolStatus(toolCalls) {
    const names = (toolCalls || []).map((t) => t.name).filter(Boolean);
    if (names.includes('create_customer')) return 'Creating customer…';
    if (names.includes('generate_products_import_excel')) return 'Creating your products…';
    if (names.includes('create_collection') || names.includes('import_catalog_groups')) {
        return 'Updating your catalog…';
    }
    if (names.some((n) => n.startsWith('update_product') || n.includes('bulk'))) {
        return 'Updating products…';
    }
    if (names.includes('open_page')) return 'Opening the right page…';
    if (names.includes('logout')) return 'Logging you out…';
    if (names.includes('apply_product_discount')) return 'Updating discounts via product sheet…';
    if (names.some((n) => n.includes('order') || n.includes('sales') || n.includes('customer'))) {
        return 'Fetching your data…';
    }
    return 'Working…';
}

function phaseHeadline(phase, isRtl) {
    if (phase === 'classifying') return isRtl ? 'بيفهم السؤال…' : 'Understanding…';
    if (phase === 'working') return isRtl ? 'يعمل…' : 'Working…';
    if (phase === 'answering') return isRtl ? 'يكتب…' : 'Writing…';
    if (phase === 'teleport') return isRtl ? 'يفتح الصفحة…' : 'Opening…';
    return isRtl ? 'يفكر…' : 'Thinking…';
}

/** Same Tabexawy mark used in the chat header. */
function MessageAvatar({ thinking = false, size = 16 }) {
    return (
        <div className={`ai-avatar ai-msg-avatar${thinking ? ' thinking' : ''}`}>
            <div className="ai-avatar-core" />
            <div className="ai-avatar-ring" />
            <TabexawyMark className="ai-avatar-mark" size={size} />
        </div>
    );
}

function normalizeStatus(text) {
    return String(text || '')
        .trim()
        .toLowerCase()
        .replace(/[.…]+$/g, '')
        .replace(/\s+/g, ' ');
}

export default function AIChat() {
    const { routedispatchercontext } = useContext(Routedispatcher);
    const { userloggedinfobjcontext } = useContext(Loggedincontext);
    const langCtx = useContext(LanguageContext);
    const isRtl = langCtx?.langdetect === 'ar';
    const instituteId = String(userloggedinfobjcontext?.currentinstinfo?.instid || '').trim();
    const userId = resolveAiUserId(userloggedinfobjcontext?.userinfo);
    const conversationStorageKey = aiConversationStorageKey(userId);
    const voiceEvalAllowed = isAiVoiceEvalInstitute(instituteId);
    const [evalOpen, setEvalOpen] = useState(false);
    const [traceOpen, setTraceOpen] = useState(false);
    const [traceTurnId, setTraceTurnId] = useState(null);
    const [apiTraces, setApiTraces] = useState([]);
    const [open, setOpen] = useState(false);
    const [panelMounted, setPanelMounted] = useState(false);
    const [panelLeaving, setPanelLeaving] = useState(false);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState([]);
    const [visibleCount, setVisibleCount] = useState(MESSAGE_PAGE_SIZE);
    const [conversationId, setConversationId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statusText, setStatusText] = useState('');
    const [statusPhase, setStatusPhase] = useState('');
    const [teleporting, setTeleporting] = useState(false);
    const [teleportLabel, setTeleportLabel] = useState('');
    const [error, setError] = useState(null);
    const teleportTimerRef = useRef(null);
    const panelCloseTimerRef = useRef(null);
    const [voiceOpen, setVoiceOpen] = useState(false);
    const [imageStudio, setImageStudio] = useState({ open: false, src: '', alt: '', messageId: null });
    const [importFile, setImportFile] = useState(null);
    const [importKind, setImportKind] = useState('');
    const fileInputRef = useRef(null);
    const [voiceNote, setVoiceNote] = useState({ phase: 'idle', elapsed: 0, bars: [] });
    const voiceRecRef = useRef(null);
    const voiceBusyRef = useRef(false);
    const inputRef = useRef('');
    const importFileRef = useRef(null);
    const importKindRef = useRef('');

    const bottomRef = useRef(null);
    const messagesRef = useRef(null);
    const answerAnchorRef = useRef(null);
    const conversationIdRef = useRef(conversationId);
    const streamBufferRef = useRef('');
    const streamFlushTimerRef = useRef(null);
    const abortControllerRef = useRef(null);
    const stickToBottomRef = useRef(true);
    const loadingOlderRef = useRef(false);
    const pendingScrollRestoreRef = useRef(null);

    useEffect(() => {
        if (!voiceEvalAllowed && voiceOpen) {
            setVoiceOpen(false);
        }
    }, [voiceEvalAllowed, voiceOpen]);

    inputRef.current = input;
    importFileRef.current = importFile;
    importKindRef.current = importKind;

    useEffect(() => {
        if (voiceNote.phase !== 'recording') return undefined;
        const timer = setInterval(() => {
            const rec = voiceRecRef.current;
            const started = rec?.startedAt || Date.now();
            const elapsed = Date.now() - started;
            const bars = rec?.analyser?.sample?.() || rec?.bars || fallbackPeaks(elapsed / 180);
            if (rec) rec.peaks = mergePeaks(rec.peaks, bars);
            setVoiceNote((prev) => ({ ...prev, elapsed, bars }));
            if (elapsed >= VOICE_NOTE_MAX_MS) {
                try {
                    if (rec?.recorder?.state === 'recording') {
                        rec.action = 'send';
                        rec.recorder.stop();
                    }
                } catch (_) {
                    /* ignore */
                }
            }
        }, 80);
        return () => clearInterval(timer);
    }, [voiceNote.phase]);

    useEffect(
        () => () => {
            const rec = voiceRecRef.current;
            try {
                if (rec?.recorder?.state === 'recording') rec.recorder.stop();
            } catch (_) {
                /* ignore */
            }
            rec?.analyser?.stop?.();
            rec?.stream?.getTracks?.().forEach((track) => track.stop());
        },
        [],
    );

    useEffect(() => {
        setAiSessionInstituteId(instituteId);
        setAiSessionInstituteName(userloggedinfobjcontext?.currentinstinfo?.instname);
        setApiTraces([]);
    }, [instituteId, userloggedinfobjcontext?.currentinstinfo?.instname]);

    useEffect(() => {
        setAiSessionUserId(userId);
        setAiSessionUserName(
            userloggedinfobjcontext?.userinfo?.name ||
                userloggedinfobjcontext?.userinfo?.username ||
                userloggedinfobjcontext?.userinfo?.fullname,
        );
    }, [
        userId,
        userloggedinfobjcontext?.userinfo?.name,
        userloggedinfobjcontext?.userinfo?.username,
        userloggedinfobjcontext?.userinfo?.fullname,
    ]);

    const mergeApiTraces = useCallback((incoming) => {
        if (!incoming?.length) return;
        setApiTraces((prev) => mergeTraces(prev, incoming));
    }, []);

    const openRequestTraces = useCallback((turnId = null) => {
        setEvalOpen(false);
        setTraceTurnId(turnId || null);
        setTraceOpen(true);
    }, []);

    const rateAnswer = useCallback(
        async (turnId, rating) => {
            if (!turnId || !rating) return;
            setMessages((prev) =>
                prev.map((m) => (m.turn_id === turnId ? { ...m, rating } : m)),
            );
            try {
                await fetch(`${aiserverbaselink}/instowner/ai/rate`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        ...aiInstituteHeaders(instituteId, userId),
                    },
                    body: JSON.stringify({
                        turn_id: turnId,
                        conversation_id: conversationIdRef.current,
                        rating,
                    }),
                });
            } catch (_) {
                /* keep optimistic rating */
            }
        },
        [instituteId, userId],
    );

    const persistConversationId = (id) => {
        const next = id || null;
        setConversationId(next);
        conversationIdRef.current = next;
        try {
            if (next) {
                localStorage.setItem(conversationStorageKey, next);
                // Migrate away from shared legacy key so users don't cross-read.
                if (userId) localStorage.removeItem('aiConversationId');
            } else {
                localStorage.removeItem(conversationStorageKey);
            }
        } catch (_) {
            /* ignore */
        }
    };

    useEffect(() => {
        if (!voiceEvalAllowed && evalOpen) {
            setEvalOpen(false);
        }
        if (!voiceEvalAllowed && traceOpen) {
            setTraceOpen(false);
        }
    }, [voiceEvalAllowed, evalOpen, traceOpen]);

    useEffect(() => {
        conversationIdRef.current = conversationId;
    }, [conversationId]);

    const scrollToBottom = useCallback((behavior = 'auto') => {
        const el = messagesRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
            if (behavior === 'smooth') {
                try {
                    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
                } catch (_) {
                    el.scrollTop = el.scrollHeight;
                }
            }
            return;
        }
        bottomRef.current?.scrollIntoView({ block: 'end' });
    }, []);

    const scrollToAnswer = useCallback((behavior = 'auto') => {
        stickToBottomRef.current = true;
        const anchor = answerAnchorRef.current;
        if (anchor && typeof anchor.scrollIntoView === 'function') {
            try {
                anchor.scrollIntoView({ block: 'nearest', behavior });
            } catch (_) {
                /* ignore */
            }
        }
        scrollToBottom(behavior === 'smooth' ? 'smooth' : 'auto');
    }, [scrollToBottom]);

    useEffect(() => {
        if (!open) return;
        if (!stickToBottomRef.current) return;
        scrollToBottom('auto');
        const frame = requestAnimationFrame(() => {
            if (stickToBottomRef.current) scrollToBottom('auto');
        });
        const timer = setTimeout(() => {
            if (stickToBottomRef.current) scrollToBottom('auto');
        }, 280);
        return () => {
            cancelAnimationFrame(frame);
            clearTimeout(timer);
        };
    }, [open, messages, loading, statusText, statusPhase, visibleCount, scrollToBottom]);

    const transcriptMessages = useMemo(
        () => messages.filter((m) => m.role === 'user' || m.role === 'assistant'),
        [messages],
    );

    const visibleMessages = useMemo(() => {
        if (transcriptMessages.length <= visibleCount) return transcriptMessages;
        return transcriptMessages.slice(transcriptMessages.length - visibleCount);
    }, [transcriptMessages, visibleCount]);

    const hasOlderMessages = transcriptMessages.length > visibleCount;

    const loadOlderMessages = useCallback(() => {
        if (!hasOlderMessages || loadingOlderRef.current) return;
        const el = messagesRef.current;
        if (!el) return;
        loadingOlderRef.current = true;
        stickToBottomRef.current = false;
        pendingScrollRestoreRef.current = { prevHeight: el.scrollHeight };
        setVisibleCount((count) => Math.min(count + MESSAGE_PAGE_SIZE, transcriptMessages.length));
    }, [hasOlderMessages, transcriptMessages.length]);

    useLayoutEffect(() => {
        const pending = pendingScrollRestoreRef.current;
        if (!pending) return;
        const el = messagesRef.current;
        pendingScrollRestoreRef.current = null;
        if (el) {
            el.scrollTop = el.scrollHeight - pending.prevHeight;
        }
        loadingOlderRef.current = false;
    }, [visibleCount, visibleMessages.length]);

    const onMessagesScroll = useCallback(() => {
        const el = messagesRef.current;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        stickToBottomRef.current = distanceFromBottom < 96;
        if (el.scrollTop < 72) {
            loadOlderMessages();
        }
    }, [loadOlderMessages]);

    const goToPath = (path, label) => {
        if (!path || typeof path !== 'string') return;
        const trimmed = path.trim();
        // Never navigate to site root / bogus hosts — that causes useless GETs to http://localhost:3000/
        if (
            trimmed === '/' ||
            trimmed === '' ||
            /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/?$/i.test(trimmed) ||
            !trimmed.startsWith('/dashboard/institute')
        ) {
            console.warn('AI navigation blocked for path:', path);
            return;
        }
        try {
            let finalPath = trimmed;
            // Product info: keep the correct section and bump _ai so the page refetches fresh data.
            if (trimmed.includes('/products/productinfo/')) {
                const qIndex = trimmed.indexOf('?');
                const base = qIndex >= 0 ? trimmed.slice(0, qIndex) : trimmed;
                const params = new URLSearchParams(qIndex >= 0 ? trimmed.slice(qIndex + 1) : '');
                if (!params.get('section')) params.set('section', 'information');
                params.set('_ai', String(Date.now()));
                finalPath = `${base}?${params.toString()}`;
            }

            // Cinematic teleport: slower fade + warp, then route
            if (teleportTimerRef.current) clearTimeout(teleportTimerRef.current);
            setTeleportLabel(label || finalPath.split('/').filter(Boolean).slice(-1)[0] || 'Opening…');
            setTeleporting(true);
            setStatusPhase('teleport');
            setStatusText(`Teleporting to ${label || 'page'}…`);

            teleportTimerRef.current = setTimeout(() => {
                try {
                    routedispatchercontext(finalPath);
                } catch (err) {
                    console.error('AI navigation failed', err);
                }
                teleportTimerRef.current = setTimeout(() => {
                    setTeleporting(false);
                    setTeleportLabel('');
                    teleportTimerRef.current = null;
                }, 720);
            }, 480);
        } catch (err) {
            console.error('AI navigation failed', err);
        }
    };

    const handleVoiceTranscript = ({ role, text }) => {
        if (!text) return;
        setMessages((prev) => [
            ...prev,
            {
                id: Date.now() + Math.random(),
                role: role === 'assistant' ? 'assistant' : 'user',
                text,
                streaming: false,
                usage: null,
                navigate: null,
                voice: true,
            },
        ]);
    };

    const handleVoiceSessionEnd = (usage) => {
        if (!usage) return;
        setMessages((prev) => [
            ...prev,
            {
                id: Date.now() + Math.random(),
                role: 'assistant',
                text: `انتهت المكالمة — **${formatCostEgp(usage)}**`,
                streaming: false,
                usage: {
                    total_tokens: usage.total_tokens,
                    cost_usd: usage.cost_usd,
                    cost_usd_display: usage.cost_usd_display,
                },
                navigate: null,
                voice: true,
            },
        ]);
    };

    const stopGeneration = () => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
    };

    const runChatRequest = async ({
        body,
        assistantId,
        signal,
        toolRound = 0,
        usageAcc = null,
        startedAt = Date.now(),
    }) => {
        if (signal?.aborted) {
            throw new DOMException('Aborted', 'AbortError');
        }

        const MAX_TOOL_ROUNDS = 8;

        let didNavigate = false;
        let sawDone = false;
        let pendingTools = null;
        let turnUsage = null;
        streamBufferRef.current = '';
        if (streamFlushTimerRef.current) {
            clearTimeout(streamFlushTimerRef.current);
            streamFlushTimerRef.current = null;
        }

        const flushStreamBuffer = () => {
            const chunk = streamBufferRef.current;
            if (!chunk) return;
            streamBufferRef.current = '';
            setMessages((prev) =>
                prev.map((m) =>
                    m.id === assistantId ? { ...m, text: (m.text || '') + chunk, streaming: true } : m,
                ),
            );
        };

        const queueDelta = (content) => {
            streamBufferRef.current += content;
            if (streamFlushTimerRef.current) return;
            streamFlushTimerRef.current = setTimeout(() => {
                streamFlushTimerRef.current = null;
                flushStreamBuffer();
            }, 16);
        };

        const handleEvent = (eventName, data) => {
            if (eventName === 'meta' && data.conversation_id) {
                persistConversationId(data.conversation_id);
            }
            if (eventName === 'meta' && data.turn_id) {
                setMessages((prev) => {
                    const lastUser = [...prev].reverse().find((m) => m.role === 'user');
                    return prev.map((m) => {
                        if (m.id === assistantId) return { ...m, turn_id: data.turn_id };
                        if (lastUser && m.id === lastUser.id) return { ...m, turn_id: data.turn_id };
                        return m;
                    });
                });
            }

            if (eventName === 'status' && data.text) {
                setStatusText(data.text);
                if (data.phase) setStatusPhase(data.phase);
            }

            if (eventName === 'navigate' && data.path && !didNavigate) {
                didNavigate = true;
                goToPath(data.path, data.label);
                setMessages((prev) =>
                    prev.map((m) =>
                        m.id === assistantId
                            ? { ...m, navigate: { path: data.path, label: data.label || data.path } }
                            : m,
                    ),
                );
            }

            if (eventName === 'delta' && data.content) {
                setStatusText('Writing…');
                setStatusPhase('answering');
                queueDelta(data.content);
            }

            if (eventName === 'tools' && Array.isArray(data.tool_calls)) {
                pendingTools = data.tool_calls;
                const label = friendlyToolStatus(data.tool_calls);
                setStatusText(label);
                setStatusPhase('working');
            }

            if (eventName === 'awaiting_tools' && data.conversation_id) {
                persistConversationId(data.conversation_id);
                // Running turn total from the server (classify + tool call so far).
                if (data.usage) turnUsage = data.usage;
            }

            if (eventName === 'openai_trace' && data?.id) {
                mergeApiTraces([data]);
                if (data.turn_id) {
                    setMessages((prev) =>
                        prev.map((m) => (m.id === assistantId ? { ...m, turn_id: data.turn_id } : m)),
                    );
                }
            }

            if (eventName === 'done') {
                sawDone = true;
                setStatusText('');
                setStatusPhase('');
                if (streamFlushTimerRef.current) {
                    clearTimeout(streamFlushTimerRef.current);
                    streamFlushTimerRef.current = null;
                }
                flushStreamBuffer();
                if (data.conversation_id) {
                    persistConversationId(data.conversation_id);
                }
                if (data.navigate?.path && !didNavigate) {
                    didNavigate = true;
                    goToPath(data.navigate.path, data.navigate.label);
                }
                // Server sends a running turn total. `complete` on done is the full user-turn bill
                // (classify + tools + answer). Do not re-sum prior rounds or we double-count.
                const usage = data.usage?.complete
                    ? data.usage
                    : mergeUsage(usageAcc, mergeUsage(turnUsage, data.usage || null));
                const elapsedMs = Math.max(
                    Number(usage?.elapsed_ms) || 0,
                    Date.now() - startedAt,
                );
                if (Array.isArray(data.openai_traces)) {
                    mergeApiTraces(data.openai_traces);
                }
                setMessages((prev) =>
                    prev.map((m) =>
                        m.id === assistantId
                            ? {
                                  ...m,
                                  text: data.message || m.text,
                                  usage: usage ? { ...usage, elapsed_ms: elapsedMs } : { elapsed_ms: elapsedMs },
                                  turn_id: data.turn_id || data.usage?.turn_id || m.turn_id || null,
                                  navigate: data.navigate || m.navigate || null,
                                  streaming: false,
                              }
                            : m,
                    ),
                );
            }

            if (eventName === 'error') {
                throw new Error(data.details || data.error || 'Unable to contact AI.');
            }
        };

        const response = await fetch(aiserverbaselink + '/instowner/ai/chat', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream',
                ...aiInstituteHeaders(instituteId, userId),
            },
            body: JSON.stringify(body),
            signal,
        });

        if (!response.ok || !response.body) {
            throw new Error('Unable to contact AI.');
        }

        await consumeSse(response, handleEvent, signal);

        if (pendingTools?.length) {
            if (toolRound >= MAX_TOOL_ROUNDS) {
                setMessages((prev) =>
                    prev.map((m) =>
                        m.id === assistantId
                            ? {
                                  ...m,
                                  text:
                                      m.text?.trim() ||
                                      'I found matching products but need you to pick one (or rephrase). Try again with the exact product name or id.',
                                  streaming: false,
                              }
                            : m,
                    ),
                );
                return;
            }
            if (signal?.aborted) {
                throw new DOMException('Aborted', 'AbortError');
            }
            setStatusText(friendlyToolStatus(pendingTools));
            setStatusPhase('working');
            const tool_results = await executeToolCalls(pendingTools, {
                signal,
                onNavigate: (path, label) => {
                    if (!didNavigate) {
                        didNavigate = true;
                        goToPath(path, label);
                        setMessages((prev) =>
                            prev.map((m) =>
                                m.id === assistantId
                                    ? { ...m, navigate: { path, label: label || path } }
                                    : m,
                            ),
                        );
                    } else {
                        goToPath(path, label);
                        setMessages((prev) =>
                            prev.map((m) =>
                                m.id === assistantId
                                    ? { ...m, navigate: { path, label: label || path } }
                                    : m,
                            ),
                        );
                    }
                },
            });
            if (signal?.aborted) {
                throw new DOMException('Aborted', 'AbortError');
            }
            setStatusText('Thinking…');
            setStatusPhase('thinking');
            await runChatRequest({
                assistantId,
                signal,
                toolRound: toolRound + 1,
                usageAcc: turnUsage || usageAcc,
                startedAt,
                body: {
                    conversation_id: conversationIdRef.current,
                    tool_results,
                },
            });
            return;
        }

        if (!sawDone && !pendingTools?.length) {
            setMessages((prev) =>
                prev.map((m) =>
                    m.id === assistantId
                        ? {
                              ...m,
                              text:
                                  m.text?.trim() ||
                                  'Sorry — Tabexawy hit a snag. Try again in a moment.',
                              streaming: false,
                          }
                        : m,
                ),
            );
        } else if (!sawDone) {
            setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, streaming: false } : m)),
            );
        }
    };

    const sendChat = async (textOverride, extras = {}) => {
        const text = String(textOverride ?? input).trim();
        const attached = importFile;
        const kind = importKind;
        if (loading) return;
        if (attached && !kind) {
            setError({
                message: isRtl
                    ? 'اختَر نوع الملف تحت المرفق: منتج أو كولكشن أو أب أو فئة.'
                    : 'Choose the file type under the attachment: product, collection, parent, or category.',
            });
            return;
        }
        if (!text && !attached && !extras.existingUserId) return;

        const kindLabel = importKindLabel(kind, isRtl);
        const displayText = extras.fromVoice
            ? text
            : text ||
              (attached
                  ? isRtl
                      ? `ارفع ملف ${kindLabel}: ${attached.name}`
                      : `Import this ${kindLabel.toLowerCase()} file: ${attached.name}`
                  : '');

        const userMessage = {
            id: extras.existingUserId || Date.now(),
            role: 'user',
            text: displayText,
            importFile: attached
                ? { name: attached.name, kind, kindLabel }
                : null,
        };
        const assistantId = Date.now() + 1;
        const startedAt = Date.now();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setMessages((prev) => [
            ...prev.map((m) =>
                extras.existingUserId && m.id === extras.existingUserId
                    ? { ...m, text: displayText, voicePending: false }
                    : m,
            ),
            ...(extras.existingUserId ? [] : [userMessage]),
            {
                id: assistantId,
                role: 'assistant',
                text: '',
                streaming: true,
                usage: null,
                navigate: null,
            },
        ]);
        setInput('');
        setImportFile(null);
        setImportKind('');
        stickToBottomRef.current = true;
        setLoading(true);
        setError(null);
        setStatusText(attached ? (isRtl ? 'بيجهّز الكتالوج…' : 'Preparing the catalog…') : 'Thinking…');
        setStatusPhase(attached ? 'working' : 'thinking');
        requestAnimationFrame(() => {
            const ta = document.querySelector('.ai-textarea');
            if (ta) ta.style.height = 'auto';
            scrollToAnswer('auto');
        });
        setTimeout(() => scrollToAnswer('auto'), 60);

        try {
            let chatMessage = text;
            if (attached) {
                const result = await convertAndImportCatalogFile({
                    file: attached,
                    kind,
                    note: text,
                    signal: controller.signal,
                    isRtl,
                    onStatus: (status) => {
                        setStatusText(status);
                        setStatusPhase('working');
                    },
                });
                if (controller.signal.aborted) {
                    throw new DOMException('Aborted', 'AbortError');
                }
                if (result.navigate?.path) {
                    goToPath(result.navigate.path, result.navigate.label);
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === assistantId
                                ? {
                                      ...m,
                                      navigate: {
                                          path: result.navigate.path,
                                          label: result.navigate.label || result.navigate.path,
                                      },
                                  }
                                : m,
                        ),
                    );
                }
                if (!result.ok) {
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === assistantId
                                ? {
                                      ...m,
                                      text:
                                          result.error ||
                                          (isRtl
                                              ? 'تعذر استيراد الملف.'
                                              : 'Tabexawy could not import that file.'),
                                      streaming: false,
                                  }
                                : m,
                        ),
                    );
                    return;
                }
                chatMessage = [
                    text || `Imported ${kind} file ${attached.name}.`,
                    '',
                    'IMPORT ALREADY COMPLETED in this turn. Confirm only.',
                    'Do not call any tools. Do not import again. Do not apply discounts to the rest of the catalog. Do not fetch product info. Mention ONLY these imported rows:',
                    JSON.stringify({
                        filename: attached.name,
                        kind,
                        row_count: result.import.row_count,
                        edits: result.convert.edits || [],
                        preview: result.import.preview,
                        open_path: result.import.open_path,
                    }),
                ].join('\n');
                setStatusText('Thinking…');
                setStatusPhase('thinking');
            }

            await runChatRequest({
                assistantId,
                signal: controller.signal,
                startedAt,
                body: {
                    conversation_id: conversationIdRef.current,
                    message: chatMessage,
                    confirm_only: Boolean(attached),
                },
            });
        } catch (err) {
            const aborted = err?.name === 'AbortError' || controller.signal.aborted;
            if (aborted) {
                setMessages((prev) =>
                    prev.map((m) =>
                        m.id === assistantId
                            ? {
                                  ...m,
                                  text: m.text?.trim()
                                      ? `${m.text.trim()}\n\n_Stopped._`
                                      : '_Stopped._',
                                  streaming: false,
                                  stopped: true,
                              }
                            : m,
                    ),
                );
            } else {
                console.error(err);
                setError(err);
                setMessages((prev) =>
                    prev.map((m) =>
                        m.id === assistantId
                            ? {
                                  ...m,
                                  text: m.text || 'Sorry — Tabexawy hit a snag. Try again in a moment.',
                                  streaming: false,
                              }
                            : m,
                    ),
                );
            }
        } finally {
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }
            setLoading(false);
            setStatusText('');
            setStatusPhase('');
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (voiceNote.phase === 'recording') {
            stopVoiceNote('send');
            return;
        }
        sendChat();
    };

    const startVoiceNote = async () => {
        if (loading || voiceOpen || voiceNote.phase !== 'idle' || voiceBusyRef.current) return;
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
            setError({
                message: isRtl ? 'الميكروفون غير متاح في هذا المتصفح.' : 'Microphone is not available in this browser.',
            });
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mime = pickRecorderMime();
            const recorder = mime
                ? new MediaRecorder(stream, { mimeType: mime })
                : new MediaRecorder(stream);
            const chunks = [];
            recorder.ondataavailable = (event) => {
                if (event.data?.size) chunks.push(event.data);
            };
            recorder.onstop = async () => {
                const rec = voiceRecRef.current;
                const action = rec?.action || 'send';
                const durationMs = Date.now() - (rec?.startedAt || Date.now());
                rec?.analyser?.stop?.();
                stream.getTracks().forEach((track) => track.stop());
                voiceRecRef.current = null;
                const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
                if (action !== 'send' || !blob.size || durationMs < 400) {
                    setVoiceNote({ phase: 'idle', elapsed: 0, bars: [] });
                    return;
                }

                const caption = String(inputRef.current || '').trim();
                const userMessageId = Date.now();
                setMessages((prev) => [
                    ...prev,
                    {
                        id: userMessageId,
                        role: 'user',
                        text: caption,
                        voicePending: true,
                    },
                ]);
                setInput('');
                setVoiceNote({ phase: 'idle', elapsed: 0, bars: [] });
                stickToBottomRef.current = true;
                requestAnimationFrame(() => scrollToAnswer('auto'));

                voiceBusyRef.current = true;
                try {
                    const result = await transcribeVoiceNote(blob, { durationMs });
                    if (!result.ok) {
                        setMessages((prev) => prev.filter((m) => m.id !== userMessageId));
                        setError({
                            message:
                                result.error ||
                                (isRtl ? 'ما قدرتش أفهم الرسالة الصوتية.' : 'Could not understand that voice note.'),
                        });
                        return;
                    }
                    const spoken = String(result.text || '').trim();
                    const combined = [caption, spoken].filter(Boolean).join('\n');
                    const attachedNow = importFileRef.current && importKindRef.current;
                    if (!combined && !attachedNow) {
                        setMessages((prev) => prev.filter((m) => m.id !== userMessageId));
                        return;
                    }
                    await sendChat(combined || caption, {
                        fromVoice: true,
                        existingUserId: userMessageId,
                    });
                } finally {
                    voiceBusyRef.current = false;
                }
            };
            const analyser = createLiveAnalyser(stream);
            voiceRecRef.current = {
                recorder,
                stream,
                analyser,
                startedAt: Date.now(),
                peaks: fallbackPeaks(),
                action: 'send',
            };
            recorder.start(80);
            setError(null);
            setVoiceNote({ phase: 'recording', elapsed: 0, bars: fallbackPeaks() });
        } catch (err) {
            setError({
                message: isRtl ? 'اسمح بالميكروفون عشان تبعت فويس نوت.' : 'Allow the microphone to send a voice note.',
            });
        }
    };

    const stopVoiceNote = (action = 'send') => {
        try {
            if (voiceRecRef.current?.recorder?.state === 'recording') {
                voiceRecRef.current.action = action;
                voiceRecRef.current.recorder.stop();
            }
        } catch (_) {
            setVoiceNote({ phase: 'idle', elapsed: 0, bars: [] });
        }
    };

    const loadConversation = async (id) => {
        if (!id || id === 'null' || id === 'undefined') {
            setMessages([]);
            return;
        }

        try {
            const response = await fetch(aiserverbaselink + '/instowner/ai/conversation/' + id, {
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    ...aiInstituteHeaders(instituteId, userId),
                },
            });

            if (!response.ok) {
                throw new Error('Unable to load conversation.');
            }

            const data = await response.json();
            if (data.conversation_id) {
                persistConversationId(data.conversation_id);
            } else {
                // Wrong user / missing — start fresh for this user.
                persistConversationId(null);
                setMessages([]);
                return;
            }
            setMessages(
                (data.messages || []).map((m, index) => ({
                    id: index,
                    role: m.role,
                    text: m.content,
                    usage: m.usage || null,
                    navigate: m.navigate || null,
                    turn_id: m.turn_id || m.usage?.turn_id || null,
                    rating: m.rating || null,
                })),
            );
            setVisibleCount(MESSAGE_PAGE_SIZE);
            stickToBottomRef.current = true;
            if (Array.isArray(data.openai_traces)) {
                mergeApiTraces(data.openai_traces);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (!userId) return;
        let stored = null;
        try {
            stored = localStorage.getItem(conversationStorageKey);
            if (!stored) {
                // One-time migrate legacy shared key into this user's key.
                const legacy = localStorage.getItem('aiConversationId');
                if (legacy) {
                    stored = legacy;
                    localStorage.setItem(conversationStorageKey, legacy);
                    localStorage.removeItem('aiConversationId');
                }
            }
        } catch (_) {
            /* ignore */
        }
        persistConversationId(stored);
        loadConversation(stored);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, conversationStorageKey, instituteId]);

    useEffect(() => {
        return () => {
            if (teleportTimerRef.current) clearTimeout(teleportTimerRef.current);
            if (panelCloseTimerRef.current) clearTimeout(panelCloseTimerRef.current);
        };
    }, []);

    const isThinking = loading && (!messages.length || messages[messages.length - 1]?.streaming);
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
    const hasAnswerText = Boolean(lastAssistant?.text?.trim());
    const apiTurnCount = useMemo(() => {
        const ids = new Set((apiTraces || []).map((t) => t.turn_id).filter(Boolean));
        return ids.size || apiTraces.length;
    }, [apiTraces]);

    const openImageStudio = useCallback((payload, messageId = null) => {
        if (!payload?.src) return;
        setImageStudio({
            open: true,
            src: payload.src,
            alt: payload.alt || 'Image',
            messageId,
        });
    }, []);

    const applyStudioImage = useCallback(({ originalSrc, editedSrc }) => {
        if (!originalSrc || !editedSrc || originalSrc === editedSrc) return;
        setMessages((prev) =>
            prev.map((m) => {
                if (m.role !== 'assistant' || typeof m.text !== 'string') return m;
                if (imageStudio.messageId && m.id !== imageStudio.messageId) return m;
                if (!m.text.includes(originalSrc)) return m;
                return {
                    ...m,
                    text: m.text.split(originalSrc).join(editedSrc),
                };
            }),
        );
    }, [imageStudio.messageId]);

    // Keep the live answer in view while Tabexawy works / streams
    useEffect(() => {
        if (!open) return;
        if (!loading && !hasAnswerText) return;
        stickToBottomRef.current = true;
        const t0 = requestAnimationFrame(() => scrollToAnswer('auto'));
        const t1 = setTimeout(() => scrollToAnswer('smooth'), 80);
        const t2 = setTimeout(() => scrollToAnswer('auto'), 320);
        return () => {
            cancelAnimationFrame(t0);
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [open, loading, hasAnswerText, statusPhase, lastAssistant?.text, statusText, scrollToAnswer]);

    const [teleportMounted, setTeleportMounted] = useState(false);
    const [teleportLeaving, setTeleportLeaving] = useState(false);

    useEffect(() => {
        if (teleporting) {
            setTeleportLeaving(false);
            setTeleportMounted(true);
            return undefined;
        }
        if (!teleportMounted) return undefined;
        setTeleportLeaving(true);
        const t = setTimeout(() => {
            setTeleportMounted(false);
            setTeleportLeaving(false);
        }, 620);
        return () => clearTimeout(t);
    }, [teleporting, teleportMounted]);

    const openChat = () => {
        if (panelCloseTimerRef.current) {
            clearTimeout(panelCloseTimerRef.current);
            panelCloseTimerRef.current = null;
        }
        setPanelLeaving(false);
        setPanelMounted(true);
        setOpen(true);
    };

    const closeChat = () => {
        if (panelLeaving) return;
        setOpen(false);
        setPanelLeaving(true);
        setVoiceOpen(false);
        if (panelCloseTimerRef.current) clearTimeout(panelCloseTimerRef.current);
        panelCloseTimerRef.current = setTimeout(() => {
            setPanelMounted(false);
            setPanelLeaving(false);
            panelCloseTimerRef.current = null;
        }, 420);
    };

    const teleportStatusLabel = teleportLabel
        ? isRtl
            ? `فتح ${teleportLabel}`
            : `Opening ${teleportLabel}`
        : isRtl
          ? 'عبور…'
          : 'Crossing over…';

    const renderTeleportStage = () => (
        <div
            className={[
                'tbx-immersive',
                'tbx-immersive--fullscreen',
                'is-teleport',
                teleportLeaving || !teleporting ? 'is-leaving' : 'is-entering',
                'phase-teleport',
            ].join(' ')}
            aria-live="polite"
            aria-busy={!teleportLeaving}
            aria-label={teleportStatusLabel}
        >
            <div className="tbx-immersive-veil" />
            <div className="tbx-immersive-letterbox top" aria-hidden="true" />
            <div className="tbx-immersive-letterbox bottom" aria-hidden="true" />
            <div className="tbx-immersive-center">
                <div className="tbx-immersive-logo-stage">
                    <div className="tbx-immersive-orb-wrap">
                        <div className="tbx-immersive-orb" aria-hidden="true">
                            <span className="tbx-immersive-orb-fluid" />
                            <span className="tbx-immersive-orb-fluid alt" />
                            <span className="tbx-immersive-orb-shine" />
                            <span className="tbx-immersive-mark">
                                <TabexawyMark size={54} />
                            </span>
                        </div>
                        <div className="tbx-immersive-orb-ripple r1" aria-hidden="true" />
                        <div className="tbx-immersive-orb-ripple r2" aria-hidden="true" />
                    </div>
                    <div className="tbx-immersive-logo-glow" aria-hidden="true" />
                </div>
                <div className="tbx-immersive-copy">
                    <div className="tbx-immersive-status-card">
                        <span className="tbx-immersive-status-dot" aria-hidden="true" />
                        <p className="tbx-immersive-status">{teleportStatusLabel}</p>
                    </div>
                </div>
            </div>
        </div>
    );

    const liveHeadline = phaseHeadline(statusPhase || 'thinking', isRtl);

    const ui = (
        <div className="ai-chat-root">
            {teleportMounted && renderTeleportStage()}

            <button
                className={`ai-fab ${panelMounted && !panelLeaving ? 'is-hiding' : ''} ${panelLeaving ? 'is-returning' : ''}`}
                onClick={openChat}
                aria-label="Open Tabexawy"
                aria-hidden={panelMounted && !panelLeaving}
                tabIndex={panelMounted && !panelLeaving ? -1 : 0}
                disabled={panelMounted && !panelLeaving}
            >
                <span className="ai-fab-ping" aria-hidden="true" />
                <div className="ai-fab-orb">
                    <TabexawyMark className="ai-fab-mark" size={26} />
                </div>
            </button>

            {panelMounted && (
                <div
                    className={`ai-chat-window ${panelLeaving ? 'is-closing' : 'is-opening'}`}
                    role="dialog"
                    aria-label="Tabexawy"
                    dir={isRtl ? 'rtl' : 'ltr'}
                >
                    <div className="ai-beta-banner" role="status">
                        <strong>Beta</strong>
                        <span>
                            Tabexawy is an early beta — it can make mistakes. Double-check important actions and use it
                            cautiously.
                        </span>
                    </div>
                    <div className="ai-header">
                        <div className="ai-header-left">
                            <div className={`ai-avatar ${isThinking ? 'thinking' : ''}`}>
                                <div className="ai-avatar-core" />
                                <div className="ai-avatar-ring" />
                                <TabexawyMark className="ai-avatar-mark" size={18} />
                            </div>
                            <div className="ai-header-copy">
                                <div className="title">
                                    Tabexawy <span className="ai-beta-pill">Beta</span>
                                </div>
                                <div className="subtitle">
                                    {statusText ||
                                        (loading
                                            ? liveHeadline
                                            : isRtl
                                              ? 'دليلك في لوحة تابكس'
                                              : 'Your Tabex dashboard guide')}
                                </div>
                            </div>
                        </div>

                        <div className="ai-header-actions">
                            {voiceEvalAllowed && (
                                <button
                                    className="ai-voice-toggle ai-api-toggle"
                                    onClick={() => openRequestTraces(null)}
                                    aria-label={isRtl ? 'طلبات OpenAI' : 'OpenAI requests'}
                                    title={isRtl ? 'طلبات OpenAI' : 'OpenAI requests'}
                                    type="button"
                                >
                                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.04em' }}>
                                        API{apiTurnCount ? ` ${apiTurnCount}` : ''}
                                    </span>
                                </button>
                            )}
                            {voiceEvalAllowed && (
                                <button
                                    className="ai-voice-toggle"
                                    onClick={() => setEvalOpen(true)}
                                    aria-label={isRtl ? 'فحوصات تابكسوي' : 'Tabexawy health checks'}
                                    title={isRtl ? 'فحوصات' : 'Health checks'}
                                    disabled={loading}
                                    type="button"
                                >
                                    <span style={{ fontSize: 13, fontWeight: 800 }}>✓</span>
                                </button>
                            )}
                            {voiceEvalAllowed && (
                                <button
                                    className="ai-voice-toggle"
                                    onClick={() => setVoiceOpen(true)}
                                    aria-label="Call Tabexawy"
                                    title="Call (beta)"
                                    disabled={loading}
                                >
                                    <CallIcon size={16} />
                                </button>
                            )}
                            <button className="ai-close" onClick={closeChat} aria-label="Close">
                                ✕
                            </button>
                        </div>
                    </div>

                    <TabexawyEvalPanel
                        open={evalOpen && voiceEvalAllowed}
                        onClose={() => setEvalOpen(false)}
                        instituteId={instituteId}
                        isRtl={isRtl}
                    />
                    <OpenAiTracePanel
                        open={traceOpen && voiceEvalAllowed}
                        onClose={() => setTraceOpen(false)}
                        traces={apiTraces}
                        onMergeTraces={mergeApiTraces}
                        instituteId={instituteId}
                        userId={userId}
                        conversationId={conversationId}
                        focusTurnId={traceTurnId}
                        isRtl={isRtl}
                    />

                    <div className="ai-messages" ref={messagesRef} onScroll={onMessagesScroll}>
                        {messages.length === 0 && (
                            <div className="ai-empty">
                                <div className="ai-empty-orb" />
                                <h3>Hi, I’m Tabexawy</h3>
                                <p>
                                    <strong>Beta:</strong> I can make mistakes — use me carefully. Ask me to open pages,
                                    walk you through tasks, or check live data — like “what are my orders today?”
                                </p>
                            </div>
                        )}

                        {hasOlderMessages && (
                            <div className="ai-history-more" aria-hidden="true">
                                Scroll up for earlier messages
                            </div>
                        )}

                        {visibleMessages.map((message) => {
                            if (message.role === 'user') {
                                return (
                                    <div key={message.id} className="message user">
                                        {message.importFile?.name && (
                                            <div className="ai-msg-file">
                                                <span className="ai-msg-file-name">{message.importFile.name}</span>
                                                {message.importFile.kindLabel ? (
                                                    <span className="ai-msg-file-kind">
                                                        {message.importFile.kindLabel}
                                                    </span>
                                                ) : null}
                                            </div>
                                        )}
                                        {message.voicePending && !message.text ? (
                                            <div className="message-text is-pending">
                                                {isRtl ? 'بيحوّل الصوت لنص…' : 'Listening…'}
                                            </div>
                                        ) : message.text ? (
                                            <div className="message-text">{message.text}</div>
                                        ) : null}
                                        {voiceEvalAllowed && message.turn_id ? (
                                            <button
                                                type="button"
                                                className="message-usage-inspect"
                                                onClick={() => openRequestTraces(message.turn_id)}
                                            >
                                                {isRtl ? 'هذا الطلب' : 'This request'}
                                            </button>
                                        ) : null}
                                    </div>
                                );
                            }

                            const streaming = Boolean(message.streaming);
                            const hasText = Boolean(message.text?.trim());
                            const topic = liveHeadline;
                            const streamStatus = statusText?.trim() || liveHeadline;
                            const sameTopicAndStream =
                                normalizeStatus(topic) === normalizeStatus(streamStatus);

                            // Live thinking / working / writing — header icon + white answer box
                            if (streaming) {
                                return (
                                    <div
                                        key={message.id}
                                        ref={answerAnchorRef}
                                        className="ai-msg-row is-assistant is-live"
                                        aria-live="polite"
                                        aria-busy="true"
                                    >
                                        <MessageAvatar thinking size={16} />
                                        <div className="message assistant">
                                            {message.navigate?.path && (
                                                <button
                                                    type="button"
                                                    className="message-nav-chip"
                                                    onClick={() =>
                                                        goToPath(
                                                            message.navigate.path,
                                                            message.navigate.label,
                                                        )
                                                    }
                                                >
                                                    Opened:{' '}
                                                    {message.navigate.label || message.navigate.path}
                                                </button>
                                            )}

                                            {!hasText && !sameTopicAndStream && (
                                                <div className="ai-live-topic">{topic}</div>
                                            )}

                                            {hasText ? (
                                                <div className="message-text">
                                                    <MarkdownMessage
                                                        content={message.text}
                                                        streaming
                                                        onNavigate={goToPath}
                                                        onOpenStudio={(payload) =>
                                                            openImageStudio(payload, message.id)
                                                        }
                                                    />
                                                </div>
                                            ) : (
                                                <div className="ai-live-activity">
                                                    {streamStatus}
                                                    <span className="streaming-cursor" aria-hidden="true">
                                                        ▍
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            }

                            // Finished reply — header icon + white bubble
                            return (
                                <div key={message.id} className="ai-msg-row is-assistant">
                                    <MessageAvatar size={16} />
                                    <div className="message assistant">
                                        {message.navigate?.path && (
                                            <button
                                                type="button"
                                                className="message-nav-chip"
                                                onClick={() =>
                                                    goToPath(
                                                        message.navigate.path,
                                                        message.navigate.label,
                                                    )
                                                }
                                            >
                                                Opened: {message.navigate.label || message.navigate.path}
                                            </button>
                                        )}
                                        <div className="message-text">
                                            <MarkdownMessage
                                                content={message.text}
                                                streaming={false}
                                                onNavigate={goToPath}
                                                onOpenStudio={(payload) =>
                                                    openImageStudio(payload, message.id)
                                                }
                                            />
                                        </div>
                                        {(usageFooterText(message.usage) || message.turn_id) && (
                                            <div className="message-usage">
                                                {usageFooterText(message.usage) ? (
                                                    <span>{usageFooterText(message.usage)}</span>
                                                ) : null}
                                                {message.turn_id ? (
                                                    <span className="message-rating" role="group" aria-label="Rate answer">
                                                        {[1, 2, 3, 4, 5].map((n) => (
                                                            <button
                                                                key={n}
                                                                type="button"
                                                                className={
                                                                    n <= (Number(message.rating) || 0)
                                                                        ? 'is-on'
                                                                        : ''
                                                                }
                                                                onClick={() =>
                                                                    rateAnswer(message.turn_id, n)
                                                                }
                                                                aria-label={`${n} star`}
                                                            >
                                                                ★
                                                            </button>
                                                        ))}
                                                    </span>
                                                ) : null}
                                                {voiceEvalAllowed &&
                                                    (message.turn_id || message.usage?.turn_id) && (
                                                        <button
                                                            type="button"
                                                            className="message-usage-inspect"
                                                            onClick={() =>
                                                                openRequestTraces(
                                                                    message.turn_id ||
                                                                        message.usage.turn_id,
                                                                )
                                                            }
                                                        >
                                                            {isRtl ? 'هذا الطلب' : 'This request'}
                                                        </button>
                                                    )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {error && <div className="ai-error">{error.message}</div>}

                        <div ref={bottomRef} />
                    </div>

                    <form className="ai-input" onSubmit={onSubmit}>
                        {importFile && (
                            <div className="ai-import-file">
                                <div className="ai-import-file-row">
                                    <span className="ai-import-file-name" title={importFile.name}>
                                        {importFile.name}
                                    </span>
                                    <button
                                        type="button"
                                        className="ai-import-file-remove"
                                        onClick={() => {
                                            setImportFile(null);
                                            setImportKind('');
                                            if (fileInputRef.current) fileInputRef.current.value = '';
                                        }}
                                        disabled={loading}
                                        aria-label={isRtl ? 'إزالة الملف' : 'Remove file'}
                                    >
                                        ×
                                    </button>
                                </div>
                                <label className="ai-import-kind">
                                    <span>
                                        {isRtl ? 'نوع الملف' : 'This file is'}
                                    </span>
                                    <select
                                        value={importKind}
                                        onChange={(e) => setImportKind(e.target.value)}
                                        disabled={loading}
                                        required
                                    >
                                        <option value="">
                                            {isRtl ? 'اختَر نوعًا' : 'Choose type'}
                                        </option>
                                        {IMPORT_KINDS.map((item) => (
                                            <option key={item.id} value={item.id}>
                                                {isRtl ? item.ar : item.en}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="ai-file-input"
                            accept={IMPORT_FILE_ACCEPT}
                            disabled={loading || voiceOpen || voiceNote.phase !== 'idle'}
                            onChange={(e) => {
                                const next = e.target.files?.[0] || null;
                                e.target.value = '';
                                if (!next) return;
                                const reason = validateCatalogImportFile(next);
                                if (reason) {
                                    setError({ message: reason });
                                    return;
                                }
                                setError(null);
                                setImportFile(next);
                            }}
                        />
                        {voiceNote.phase === 'recording' ? (
                            <div className="ai-vn-dock" dir="ltr">
                                <button
                                    type="button"
                                    className="ai-vn-cancel"
                                    onClick={() => stopVoiceNote('cancel')}
                                    aria-label={isRtl ? 'حذف الفويس نوت' : 'Delete voice note'}
                                    title={isRtl ? 'حذف' : 'Delete'}
                                >
                                    <BsTrash3 size={16} />
                                </button>
                                <div className="ai-vn-live" aria-hidden="true">
                                    {(voiceNote.bars?.length ? voiceNote.bars : fallbackPeaks()).map(
                                        (height, index) => (
                                            <span
                                                key={index}
                                                style={{
                                                    height: `${6 + (Number(height) || 0.12) * 22}px`,
                                                }}
                                            />
                                        ),
                                    )}
                                </div>
                                <span className="ai-vn-rec-time">
                                    <i className="ai-vn-rec-dot" />
                                    {formatNoteTimer(voiceNote.elapsed)}
                                </span>
                                <button
                                    type="button"
                                    className="ai-vn-send"
                                    onClick={() => stopVoiceNote('send')}
                                    aria-label={isRtl ? 'إرسال الفويس نوت' : 'Send voice note'}
                                    title={isRtl ? 'إرسال' : 'Send'}
                                >
                                    <BsSendFill size={15} />
                                </button>
                            </div>
                        ) : (
                        <div className="ai-input-shell">
                            <button
                                type="button"
                                className="ai-vn-mic"
                                disabled={loading || voiceOpen}
                                aria-label={isRtl ? 'فويس نوت' : 'Voice note'}
                                title={isRtl ? 'اضغط للتسجيل — 10 ثواني' : 'Tap to record — 10 seconds'}
                                onClick={startVoiceNote}
                            >
                                <BsMicFill size={18} />
                            </button>
                            <button
                                type="button"
                                className="ai-clip"
                                disabled={loading || voiceOpen}
                                title={isRtl ? 'ملف واحد: Excel أو CSV أو PDF أو Word' : 'One file: Excel, CSV, PDF, or Word'}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <BsPaperclip size={18} />
                            </button>
                            <textarea
                                className="ai-textarea"
                                value={input}
                                rows={1}
                                onChange={(e) => {
                                    setInput(e.target.value);
                                    const el = e.target;
                                    el.style.height = 'auto';
                                    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        const canSend =
                                            !loading &&
                                            (input.trim() || (importFile && importKind));
                                        if (canSend) {
                                            e.currentTarget.form?.requestSubmit();
                                        }
                                    }
                                }}
                                placeholder={
                                    loading
                                        ? 'Stop to send a new message…'
                                        : importFile
                                          ? isRtl
                                              ? 'ملاحظة اختيارية ثم أرسل…'
                                              : 'Optional note, then send…'
                                          : 'Ask Tabexawy anything…'
                                }
                                disabled={loading || voiceOpen}
                            />
                            {loading ? (
                                <button
                                    type="button"
                                    className="ai-stop"
                                    onClick={stopGeneration}
                                    aria-label="Stop"
                                    title="Stop"
                                >
                                    <span className="ai-stop-icon" aria-hidden="true" />
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    disabled={!input.trim() && !(importFile && importKind)}
                                    aria-label="Send"
                                >
                                    ➤
                                </button>
                            )}
                        </div>
                        )}
                    </form>

                    {voiceEvalAllowed && (
                        <VoiceMode
                            open={voiceOpen}
                            onClose={() => setVoiceOpen(false)}
                            onNavigate={(path, label) => goToPath(path, label)}
                            onTranscript={handleVoiceTranscript}
                            onSessionEnd={handleVoiceSessionEnd}
                        />
                    )}

                    <ImageStudio
                        open={imageStudio.open}
                        src={imageStudio.src}
                        alt={imageStudio.alt}
                        onClose={() =>
                            setImageStudio({ open: false, src: '', alt: '', messageId: null })
                        }
                        onApply={applyStudioImage}
                    />
                </div>
            )}
        </div>
    );

    if (typeof document === 'undefined') return null;
    return createPortal(ui, document.body);
}
